import { useState, useEffect, Suspense, useMemo } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { createTheme, ThemeProvider, CssBaseline } from "@mui/material";
import useTitanApp from "./hooks/useTitanApp";
import "./assets/css/Settings/Settings.scss";
import "./assets/css/Prescriptionheader.scss";
import { setSentryUser } from "./services/logService";
import config from "./config";
import Chat from "./components/Chat/Chat";
import useIdleTimeout from "./hooks/useIdleTimeout";
import Intercom from "@intercom/messenger-js-sdk";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import {
  getIntercomDetail,
  setShowIdlePopup,
} from "./dispatchers/appDispatchers";
import GAListener from "./utils/GAListener";
import IdleWarning from "./components/IdleWarning/IdleWarning";
import ErrorModal from "./components/ErrorPage/ErrorModal";
import LottieLoader from "./components/controls/LottieLoader";
import AccountExpiredModal from "./components/ErrorPage/AccountExpiredModal";
import {
  showInfo,
} from "./components/ToastedMessage/ToastedMessage";

const { intercomMessengerId, intercomMessengerRegion } = config;

const theme = createTheme({
  typography: {
    fontFamily: [
      "SF Pro Display",
      "SF Pro Text",
      "-apple-system",
      "BlinkMacSystemFont",
      "ui-sans-serif",
      "system-ui",
      "sans-serif",
      "Apple Color Emoji",
      "Segoe UI Emoji",
      "Segoe UI Symbol",
      "Noto Color Emoji",
    ].join(","),
  },
});

export default function App({ root }) {
  const { idleTimer } = useIdleTimeout();
  const {
    auth,
    loadRoutes,
    appState,
    patientState,
    accountExpired,
    setAccountExpired,
    accountExpiredReason,
  } = useTitanApp();
  const { intercomDetail, showIdlePopup } = appState;
  const { consultationStarted } = patientState;
  const [router] = useState(() => createBrowserRouter(loadRoutes(root) as any));

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorState, setErrorState] = useState(null);

  useEffect(() => {
    if (config?.environmentName !== "development") {
      setSentryUser(auth?.user);
    }
  }, [auth?.user]);

  useEffect(() => {
    if (consultationStarted) {
      idleTimer.pause();
    } else {
      idleTimer.resume();
      idleTimer.reset();
    }
  }, [consultationStarted, idleTimer]);

  useEffect(() => {
    const handleErrorNavigation = (event) => {
      const errorData = event.detail;

      // For 409 Conflict errors, show info toast instead of modal
      if (errorData.code === "409") {
        showInfo(
          errorData.message || "This resource already exists. Please try again.",
        );
        return;
      }

      // For all other errors, show the error modal
      setErrorState(errorData);
      setShowErrorModal(true);

      // Also store in sessionStorage as backup
      sessionStorage.setItem("lastError", JSON.stringify(errorData));
    };

    window.addEventListener("navigateToError", handleErrorNavigation);

    return () => {
      window.removeEventListener("navigateToError", handleErrorNavigation);
    };
  }, []);

  useEffect(() => {
    const syncTabs = (event) => {
      if (event.key === "titan_logout_event") {
        window.location.reload();
      }

      if (event.key === "session-continue") {
        if (idleTimer) {
          idleTimer.reset();
        }
        setShowIdlePopup(false);
      }
      if (event.key === "titan_last_active") {
        idleTimer?.reset();
        setShowIdlePopup(false);
      }
    };

    window.addEventListener("storage", syncTabs);
    return () => window.removeEventListener("storage", syncTabs);
  }, [idleTimer]);

  useEffect(() => {
    const markActive = () => {
      localStorage.setItem("titan_last_active", Date.now().toString());
    };

    window.addEventListener("mousemove", markActive);
    window.addEventListener("keydown", markActive);
    window.addEventListener("click", markActive);

    return () => {
      window.removeEventListener("mousemove", markActive);
      window.removeEventListener("keydown", markActive);
      window.removeEventListener("click", markActive);
    };
  }, []);

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorState(null);
    sessionStorage.removeItem("lastError");
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Chat />

      {!consultationStarted && showIdlePopup && <IdleWarning />}
      {showErrorModal && errorState && (
        <ErrorModal errorState={errorState} onClose={handleCloseErrorModal} />
      )}
      {accountExpired && !showErrorModal && (
        <AccountExpiredModal
          reason={accountExpiredReason as "expired" | "not_started"}
          onClose={() => setAccountExpired(false)}
        />
      )}
      <Suspense fallback={<LottieLoader />}>
        <RouterProvider router={router} />
      </Suspense>
    </ThemeProvider>
  );
}
